
import { supabase } from '@/lib/supabase';

export interface ErrorLog {
  id?: string;
  type: 'react' | 'uncaught' | 'unhandledrejection' | 'initialization' | 'api' | 'other';
  message: string;
  stack?: string;
  component?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  timestamp?: string;
  url?: string;
  userAgent?: string;
  appVersion?: string;
  userId?: string | null;
}

const LOCAL_STORAGE_KEY = 'supplymate_error_logs';
const MAX_STORED_LOGS = 50;

export function initializeErrorLogging(): void {
  console.log('Initializing error logging system');
  
  // Make sure we have a place to store logs
  if (!localStorage.getItem(LOCAL_STORAGE_KEY)) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([]));
  }
  
  // Try to sync any stored logs on startup
  syncErrorLogsToServer().catch(err => {
    console.error('Failed to sync error logs during initialization:', err);
  });
}

export async function logError(error: ErrorLog): Promise<void> {
  try {
    // Add metadata
    const enhancedError: ErrorLog = {
      ...error,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      appVersion: '1.0.0', // Replace with actual app version when available
    };
    
    // Try to get current user if available
    try {
      const { data: { user } } = await supabase.auth.getUser();
      enhancedError.userId = user?.id || null;
    } catch (err) {
      console.error('Could not get user ID for error log:', err);
    }
    
    console.log('Logging error:', enhancedError);
    
    // Store locally first
    storeErrorLocally(enhancedError);
    
    // Try to send to server immediately, but don't wait for it
    sendErrorToServer(enhancedError).catch(err => {
      console.error('Failed to send error to server:', err);
    });
  } catch (e) {
    console.error('Error in logError function:', e);
  }
}

function storeErrorLocally(error: ErrorLog): void {
  try {
    const storedLogs = getStoredLogs();
    
    // Add new log to the beginning
    storedLogs.unshift(error);
    
    // Limit the number of stored logs
    const trimmedLogs = storedLogs.slice(0, MAX_STORED_LOGS);
    
    // Save back to localStorage
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(trimmedLogs));
  } catch (e) {
    console.error('Failed to store error locally:', e);
  }
}

function getStoredLogs(): ErrorLog[] {
  try {
    const logsStr = localStorage.getItem(LOCAL_STORAGE_KEY);
    return logsStr ? JSON.parse(logsStr) : [];
  } catch (e) {
    console.error('Failed to get stored logs:', e);
    return [];
  }
}

export async function sendErrorToServer(error: ErrorLog): Promise<void> {
  try {
    // Prepare the data to match the Supabase schema
    const errorForSupabase = {
      message: error.message,
      stack: error.stack,
      type: error.type,
      component: error.component,
      source: error.source,
      user_id: error.userId,
      timestamp: error.timestamp,
      url: error.url,
      user_agent: error.userAgent,
      app_version: error.appVersion
    };
    
    // Send to Supabase if available and connected
    const { error: supabaseError, data } = await supabase
      .from('error_logs')
      .insert(errorForSupabase);
    
    if (supabaseError) {
      console.error('Failed to save error to Supabase:', supabaseError);
      return;
    }
    
    console.log('Error saved to Supabase successfully');
  } catch (e) {
    console.error('Failed to send error to server:', e);
    // We don't re-throw here to prevent cascading errors
  }
}

export async function syncErrorLogsToServer(): Promise<void> {
  try {
    const storedLogs = getStoredLogs();
    if (storedLogs.length === 0) {
      return;
    }
    
    console.log(`Syncing ${storedLogs.length} stored error logs to server`);
    
    // Try to sync each log, keep track of successful ones
    const successfulIndices: number[] = [];
    
    await Promise.all(
      storedLogs.map(async (log, index) => {
        try {
          await sendErrorToServer(log);
          successfulIndices.push(index);
        } catch (e) {
          console.error(`Failed to sync error log #${index}:`, e);
        }
      })
    );
    
    // Remove successfully synced logs from storage
    if (successfulIndices.length > 0) {
      const newStoredLogs = storedLogs.filter((_, index) => 
        !successfulIndices.includes(index)
      );
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newStoredLogs));
      console.log(`Successfully synced and removed ${successfulIndices.length} error logs`);
    }
  } catch (e) {
    console.error('Error in syncErrorLogsToServer:', e);
  }
}

// Utility to get logs for displaying in the app
export function getErrorLogs(): ErrorLog[] {
  return getStoredLogs();
}

// Clear all stored logs
export function clearErrorLogs(): void {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([]));
}
